import {
  Component, OnInit, ChangeDetectionStrategy, Input, Output, EventEmitter, ViewChild, ElementRef, inject, viewChild, ViewContainerRef,
  effect,
} from '@angular/core';
import { BrowserHelper, DynamicComponentsHelper, PanelResizerComponent, TooltipDirective, HtmlifyComponent } from '@tailormap-viewer/shared';
import { BehaviorSubject, map } from 'rxjs';
import { LayerTreeNodeWithLayerModel } from '../../../map/models/layer-tree-node-with-layer.model';
import { TocFeatureRegistrationService } from '../services/toc-feature-registration.service';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { LayerDetailsComponent } from './layer-details/layer-details.component';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'tm-toc-node-details',
    templateUrl: './toc-node-details.component.html',
    styleUrls: ['./toc-node-details.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        PanelResizerComponent,
        MatIconButton,
        TooltipDirective,
        MatIcon,
        LayerDetailsComponent,
        AsyncPipe,
        HtmlifyComponent,
    ],
})
export class TocNodeDetailsComponent implements OnInit {

  private tocFeatureRegistrationService = inject(TocFeatureRegistrationService);

  private detailsContent = viewChild('detailsContent', { read: ViewContainerRef });

  @Input()
  public node: LayerTreeNodeWithLayerModel | null | undefined = null;

  @Output()
  public closeDetails = new EventEmitter<void>();

  @ViewChild('detailsPanel', { static: false, read: ElementRef })
  private detailsPanel: ElementRef | undefined;

  private panelHeightSubject = new BehaviorSubject<number | undefined>(undefined);
  public panelHeight$ = this.panelHeightSubject.asObservable().pipe(map(height => height ? `${height}px` : undefined));

  constructor() {
    effect(() => {
      const detailsContent = this.detailsContent();
      const components = this.tocFeatureRegistrationService.registeredAdditionalFeatures();
      if (!detailsContent) {
        return;
      }
      DynamicComponentsHelper.createComponentsForPosition(components, {
         'layerDetails': detailsContent,
      });
    });
  }

  public ngOnInit(): void {
    this.updateHeight();
  }

  public panelResized(delta: number) {
    this.updateHeight(delta);
  }

  private updateHeight(delta?: number) {
    const el = this.detailsPanel?.nativeElement as HTMLElement;
    if (!el) {
      return;
    }
    const MIN_HEIGHT = 90;
    const MIN_TOC_TREE_HEIGHT = 100;
    // some pretty specific TOC logic, since this panel is intended as part of TOC.
    // if inside TOC panel, max height is panel height - toc tree height, else max height 80vh
    const parentPanel = el.closest<HTMLElement>('.dialog-content');
    const tocHeader = el.querySelector<HTMLElement>('.toc-header');
    const MAX_HEIGHT = parentPanel && tocHeader
      ? parentPanel.offsetHeight - tocHeader.offsetHeight - MIN_TOC_TREE_HEIGHT
      : BrowserHelper.getScreenHeight() * 0.8;
    const panelHeight = Math.max(MIN_HEIGHT, Math.min(el.offsetHeight - (delta || 0), MAX_HEIGHT));
    this.panelHeightSubject.next(panelHeight);
  }

  public close() {
    this.closeDetails.emit();
  }

}
